<template>
  <div class="skeleton-banner-wrapper">
    <div class="skeleton-banner-carousel">
      <!-- Banner Image Placeholder -->
      <div class="skeleton-banner-slide">
        <div class="skeleton-banner-image">
          <div class="skeleton-shimmer"></div>
        </div>
      </div>

      <!-- Indicator Dots Placeholder -->
      <div class="skeleton-indicators">
        <div
          v-for="index in 3"
          :key="`indicator-${index}`"
          class="skeleton-indicator"
          :class="{ active: index === 1 }"
        >
          <div class="skeleton-shimmer"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SkeletonBanner'
}
</script>

<style lang="scss" scoped>
.skeleton-banner-wrapper {
  width: 100%;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    margin-bottom: 15px;
  }
}

.skeleton-banner-carousel {
  position: relative;
  width: 100%;
  background: linear-gradient(
    135deg,
    rgba(26, 26, 46, 0.6),
    rgba(22, 33, 62, 0.6)
  );
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
  }
}

.skeleton-banner-slide {
  position: relative;
  width: 100%;
  aspect-ratio: 16/6;
  min-height: 200px;

  @media (max-width: 1200px) {
    aspect-ratio: 16/7;
  }

  @media (max-width: 768px) {
    aspect-ratio: 16/8;
    min-height: 160px;
  }

  @media (max-width: 480px) {
    aspect-ratio: 16/9;
    min-height: 140px;
  }
}

.skeleton-banner-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(45, 45, 65, 0.4),
    rgba(35, 45, 75, 0.4),
    rgba(25, 35, 55, 0.4)
  );
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    border-radius: 8px;
  }
}

.skeleton-indicators {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 2;

  @media (max-width: 768px) {
    bottom: 15px;
    gap: 6px;
  }

  @media (max-width: 480px) {
    bottom: 12px;
    gap: 5px;
  }
}

.skeleton-indicator {
  position: relative;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  overflow: hidden;
  transition: all 0.3s ease;

  &.active {
    background: rgba(255, 255, 255, 0.6);
    width: 24px;
    border-radius: 6px;
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;

    &.active {
      width: 20px;
      border-radius: 5px;
    }
  }

  @media (max-width: 480px) {
    width: 8px;
    height: 8px;

    &.active {
      width: 16px;
      border-radius: 4px;
    }
  }
}

// Shimmer animation
.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.06),
    transparent
  );
  animation: shimmer 3s infinite;
  border-radius: inherit;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Additional styling to match BannerSlide component structure
.skeleton-banner-carousel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.1) 0%,
    rgba(0, 0, 0, 0.3) 100%
  );
  z-index: 1;
  border-radius: inherit;
  pointer-events: none;
}

// Performance optimizations
.skeleton-banner-wrapper {
  will-change: contents;
  transform: translate3d(0, 0, 0);
}

// Match container fluid styling
.skeleton-banner-carousel {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
}
</style>
